import React, { useRef } from 'react';
import { FormattedMessage } from '@kyruus/intl';
import styled from '@emotion/styled';
import CancelCircleOutline from '@kyruus/react-ikons/CancelCircleOutline';
import Button from '@kyruus/button';
import Snackbar, { Button as SnackbarButton } from '@kyruus/snackbar';
import { fromTheme } from '@kyruus/ui-theme';

import ProviderList, { FROM_SEARCH_MAP } from '../../../shared/ProviderListV9';
import LoadMoreButton from '../../../shared/LoadMoreButton';
import SideSheet from '../SideSheet';
import MapCard from '../MapCard';
import LoadingOverlay from '../../../shared/loading';

import SortMenu from '../../../shared/sort-menu';

import { messages } from '../../lib/messages';
import { MapsNoResultsBanner, MapsErrorBanner } from '../ErrorBanner';
import {
  PROVIDER_DRAWER_OPEN,
  RENDER_PROVIDERS,
  FETCHING_PROVIDERS,
  LOADING_MORE_PROVIDERS,
  RENDER_SINGLE_LOCATION,
  NO_RESULTS,
  ERROR,
  IDLE,
  SNACKBAR,
  SHOW
} from '../../../machines/map-search/map-search-states';
import {
  CLOSE_PROVIDER_DRAWER,
  LOAD_MORE_PROVIDERS,
  CLOSE_SNACKBAR
} from '../../../machines/map-search/map-search-actions';
import {
  getProvidersFromV9Results,
  getSlots,
  getTotalProvidersFromV9Results
} from '../../../behaviors/search-map-v9';
import { getRelativeParameterizedBookingUrl } from '../../../utils/getRelativeParameterizedBookingUrl';
import {
  CLICK_CLOSE_PROVIDER_DRAWER,
  CLICK_LOAD_MORE_PROVIDERS,
  HIDE_HOW_TO_PIN,
  CLICK_MAPS_PROVIDER_TILE,
  CLICK_MAPS_PROVIDER_TILE_BOOK_APPT
} from '../../lib/constants';
import {
  ProviderDrawerContainer,
  ProviderDrawerCloseContainer,
  ProviderDrawerSortContainer,
  ProviderDrawerContentWrapper
} from './styles';
import { SearchSummary } from '../../../shared/SearchSummary';
import useKeyboardAccessibilityFocus from '../../hooks/keyboard-accessibility-focus';
import { getApptOptions } from '../../../utils/map-search';
import { gMapLatLngForMloc } from '../../lib/utils';
import { useKeydownListener } from '../../../utils/hooks';

const AbsoluteSnackBar = styled(Snackbar)`
  position: absolute;
  & .MuiPaper-root {
    color: ${fromTheme('color_text_contrast')};
  }
`;

function LargeView({
  intl,
  state,
  send,
  onLocationLinkClick,
  currentQuery,
  config,
  searchSummary,
  sortOptions,
  getUpdatedLocations,
  searchParams,
  log,
  location,
  customerCode,
  productName,
  gmapRef
}) {
  const { providersForDrawer, hasMoreProvidersToLoad, selectedLocation } =
    state.context;

  // create a ref for the close button so we can auto-focus it when the drawer is opened
  // (mainly for keyboard accessibility tabbing)
  const closeButtonRef = useRef();

  const isDrawerInLoadingState =
    state.matches(`${PROVIDER_DRAWER_OPEN}.${FETCHING_PROVIDERS}`) ||
    state.matches(`${PROVIDER_DRAWER_OPEN}.${LOADING_MORE_PROVIDERS}`);

  // autofocus the close button when the drawer is opened
  // (so keyboard tabbing enters the drawer)
  const { focusPin } = useKeyboardAccessibilityFocus(
    closeButtonRef,
    state,
    isDrawerInLoadingState
  );

  const onCloseProviderDrawerClick = () => {
    log(CLICK_CLOSE_PROVIDER_DRAWER);

    // recenter map to pin
    if (gmapRef.current) {
      gmapRef.current.panTo(gMapLatLngForMloc(selectedLocation));
    }
    // put focus back onto map pin (for kb accessibility)
    focusPin();

    send(CLOSE_PROVIDER_DRAWER);
  };

  const onLoadMoreProvidersClick = () => {
    log(CLICK_LOAD_MORE_PROVIDERS);
    send(LOAD_MORE_PROVIDERS);
  };

  // close drawer on escape key
  useKeydownListener('Escape', () => {
    if (state.matches(`${PROVIDER_DRAWER_OPEN}`) && !isDrawerInLoadingState) {
      onCloseProviderDrawerClick();
    }
  });

  const closeSnackbar = () => {
    log(HIDE_HOW_TO_PIN);
    send(CLOSE_SNACKBAR);
  };

  const providers = getProvidersFromV9Results(providersForDrawer) ?? [];
  const { slots, slotsStatus } = getSlots(providers);

  let drawerContent = null;

  // drawer state for bookable location/urgent care clinic
  if (state.matches(`${PROVIDER_DRAWER_OPEN}.${RENDER_SINGLE_LOCATION}`)) {
    drawerContent = (
      <React.Fragment>
        <MapCard
          locationData={selectedLocation}
          currentQuery={currentQuery}
          ctaText={intl.formatMessage(messages.bookAppointmentOnline)}
          bookOnlineUrl={getRelativeParameterizedBookingUrl({
            provider: providers[0],
            config,
            location
          })}
          onMobileCancelHandler={onCloseProviderDrawerClick}
          onLocationClick={onLocationLinkClick}
        />
      </React.Fragment>
    );
  }

  // drawer state for lists of providers at a location
  // display current contents during load more (loading overlay is in parent)
  if (
    state.matches(`${PROVIDER_DRAWER_OPEN}.${LOADING_MORE_PROVIDERS}`) ||
    state.matches(`${PROVIDER_DRAWER_OPEN}.${RENDER_PROVIDERS}`)
  ) {
    drawerContent = (
      <React.Fragment>
        <MapCard
          locationData={selectedLocation}
          currentQuery={currentQuery}
          onMobileCancelHandler={onCloseProviderDrawerClick}
          onLocationClick={onLocationLinkClick}
        />

        <ProviderDrawerSortContainer>
          <SearchSummary
            totalProviders={getTotalProvidersFromV9Results(providersForDrawer)}
            searchSummary={searchParams}
            getUpdatedSearch={getUpdatedLocations}
            locationName={selectedLocation.name}
            log={log}
          />
          <SortMenu
            searchSummary={searchParams}
            sortOptions={sortOptions}
            getUpdatedSearch={getUpdatedLocations}
          />
        </ProviderDrawerSortContainer>

        <ProviderList
          providers={providers}
          customerCode={customerCode}
          productName={productName}
          config={config}
          searchSummary={searchSummary}
          log={log}
          shouldAppendAnalyticsParamsBookOnlineUrl={true}
          profileSummaryParams={{
            from: FROM_SEARCH_MAP,
            location_id: selectedLocation.id
          }}
          providerCallbackFunc={(provider) =>
            log(CLICK_MAPS_PROVIDER_TILE, { provider_id: provider.id })
          }
          bookingCallbackFunc={(provider) =>
            log(CLICK_MAPS_PROVIDER_TILE_BOOK_APPT, {
              provider_id: provider.id
            })
          }
          apptOptions={getApptOptions(
            providersForDrawer._result,
            selectedLocation.id
          )}
          slots={slots}
          slotsStatus={slotsStatus}
        />
        {hasMoreProvidersToLoad && (
          <LoadMoreButton onLoadMoreProvidersClick={onLoadMoreProvidersClick} />
        )}
      </React.Fragment>
    );
  }

  if (state.matches(`${PROVIDER_DRAWER_OPEN}.${NO_RESULTS}`)) {
    drawerContent = (
      <MapsNoResultsBanner
        messages={messages}
        showBackToSearchResultLink={false}
        log={log}
      />
    );
  }

  if (state.matches(`${PROVIDER_DRAWER_OPEN}.${ERROR}`)) {
    drawerContent = <MapsErrorBanner messages={messages} log={log} />;
  }

  return (
    <React.Fragment>
      <SideSheet
        dataTestId="SideSheet"
        anchor="left"
        isOpen={state.matches(PROVIDER_DRAWER_OPEN)}
        width="60vw"
        closeDrawer={onCloseProviderDrawerClick}
      >
        <ProviderDrawerContainer>
          <LoadingOverlay loading={isDrawerInLoadingState} />
          <ProviderDrawerCloseContainer>
            <Button
              mode="flat"
              onClick={onCloseProviderDrawerClick}
              aria-label="Close Provider Drawer"
              className="closeBtn"
              data-testid="CloseProviderDrawerButton"
              tabindex="1"
              ref={closeButtonRef}
            >
              <CancelCircleOutline size="25px" />
            </Button>
          </ProviderDrawerCloseContainer>
          <ProviderDrawerContentWrapper data-testid="ProviderDrawerContent">
            {drawerContent}
          </ProviderDrawerContentWrapper>
        </ProviderDrawerContainer>
      </SideSheet>
      <AbsoluteSnackBar
        data-testid="MapSnackbar"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        open={state.matches({
          [IDLE]: {
            [SNACKBAR]: SHOW
          }
        })}
        message={intl.formatMessage(messages.mapSnackbarMessage)}
        action={
          <SnackbarButton
            data-testid="MapSnackbarCloseButton"
            onClick={closeSnackbar}
            key="close"
            color="inherit"
          >
            <FormattedMessage {...messages.mapSnackbarButton} />
          </SnackbarButton>
        }
      ></AbsoluteSnackBar>
    </React.Fragment>
  );
}

export default LargeView;
